//
// apps/vendor/src/pages/OrderShared.tsx
//
// Phase 6 — View a share-linked order (Mode B).
//
// This page is rendered inside the authenticated vendor app when the viewer
// clicks into a Mode B row from OrderInbox. It pulls the Order detail via
// the authenticated AppSync session (NOT a bare share-link JWT — that path
// lives in apps/shared-access for unauthenticated recipients). The
// allowed_actions surface determines which UI affordances render.

import { useParams } from "react-router-dom";
import { useFederationOrderDetailQuery } from "../__generated__/graphql";
import { ShareLinkBanner } from "../components/ShareLinkBanner";
import { vendorFailureCopy, vendorLoadFailureSentence } from "../lib/vendor-failure-copy";

const LOAD_ORDER_FAILURE_SENTENCE = vendorLoadFailureSentence("order");

export function OrderShared() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ?? "";

  const { data, loading, error } = useFederationOrderDetailQuery({
    variables: { id: orderId },
    fetchPolicy: "cache-and-network",
    skip: !id,
  });

  if (!id) return <p>Missing order id.</p>;
  if (loading && !data) return <p>Loading order…</p>;
  if (error) {
    return (
      <p role="alert">
        {vendorFailureCopy(error, { generic: LOAD_ORDER_FAILURE_SENTENCE })}
      </p>
    );
  }

  const order = data?.order;
  if (!order) return <p>Order not accessible.</p>;

  // For Phase 6, the share link metadata is not carried inline in the Order
  // payload; we render a generic "shared" banner. A dedicated
  // orderShareLinkForViewer query is left as a follow-up TODO (MOL-670).
  return (
    <article className="order-shared">
      <ShareLinkBanner orderId={order.id} />

      <h1>Order {order.barcodeValue ?? order.id}</h1>
      <dl>
        <dt>Type</dt>
        <dd>{order.orderType}</dd>
        <dt>Status</dt>
        <dd>{order.status}</dd>
        <dt>Due</dt>
        <dd>{order.estimatedCompletionAt ?? "—"}</dd>
      </dl>

      <section aria-label="Products">
        <h2>Products</h2>
        <ul>
          {order.orderProducts?.map((p) => (
            <li key={p.id}>
              {p.productType?.name ?? p.productTypeId} · qty {p.quantity}
            </li>
          ))}
        </ul>
      </section>

      <section aria-label="Files">
        <h2>Files</h2>
        <ul>
          {order.files.edges.map(({ node }) => (
            <li key={node.id}>
              {node.filename} <small>({node.mimeType})</small>
            </li>
          ))}
          {order.files.edges.length === 0 && <li>No files shared.</li>}
        </ul>
      </section>
    </article>
  );
}
